import { RouteObject } from "react-router-dom";
import { lazy, Suspense } from "react";
import ProtectedRoute from "./ProtectedRoute";
import PermissionRoute from "./PermissionRoute";
import OwnerRoute from "./OwnerRoute";
import RemoteLoginPage from "@/features/auth/pages/RemoteLoginPage";
import WhatsAppTemplateBuilder from "@/features/whats-app-crm/pages/TemplateBuilder";
import ViewTemplatePage from "@/features/whats-app-crm/components/ViewTemplatePage";
import WhatsAppLinkGenerator from '@/features/free-tools/pages/WhatsAppLinkGenerator';
import WhatsAppQRGenerator from '@/features/free-tools/pages/WhatsAppQRGenerator';
import WhatsAppWidgetGenerator from '@/features/free-tools/pages/WhatsAppWidgetGenerator';

const TemplateLibrary = lazy(() =>
  import("@/features/whats-app-crm/pages/TemplateLibrary")
);

const LoginPage = lazy(() =>
  import("@/features/auth/pages/LoginPage")
);

const RegisterPage = lazy(() =>
  import("@/features/auth/pages/RegisterPage")
);

const VerifyOtpPage = lazy(() =>
  import("@/features/auth/pages/VerifyOtpPage")
);

const ForgotPasswordPage = lazy(() =>
  import("@/features/auth/pages/ForgotPasswordPage")
);

const ResetPasswordPage = lazy(() =>
  import("@/features/auth/pages/ResetPasswordPage")
);


const NotFound = lazy(() =>
  import("@/features/error/pages/NotFound")
);

const Plans = lazy(() =>
  import("@/features/plan/pages/Plans")
);

const Setting = lazy(() =>
  import("@/features/setting/pages/Setting")
);

const AdminSettingsPage = lazy(() =>
  import("@/features/setting/pages/AdminSettingsPage")
);

const InstagramConnectionPage = lazy(() =>
  import("@/features/instagram-automation/pages/InstagramConnectionPage")
);

const InstagramAutomationListPage = lazy(() =>
  import("@/features/instagram-automation/pages/InstagramAutomationListPage")
);

const InstagramAutomationBuilderPage = lazy(() =>
  import("@/features/instagram-automation/pages/InstagramAutomationBuilderPage")
);

const InstagramAutomationDetailPage = lazy(() =>
  import("@/features/instagram-automation/pages/InstagramAutomationDetailPage")
);

// AI Chatbot v1 Pages
const KnowledgeSources = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/KnowledgeSources")
);

const KnowledgeSourceDetail = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/KnowledgeSourceDetail")
);

const AiChatbotV1Settings = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/Settings")
);

const AiChatbotV1Leads = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/Leads")
);

const AiChatbotV1TestChat = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/TestChat")
);

const AiChatbotV1Usage = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/Usage")
);

const TeamManagement = lazy(() =>
  import("@/features/team/pages/TeamManagement")
);
const RolesAndPermissions = lazy(() =>
  import("@/features/team/pages/RolesAndPermissions")
);
const CreateRolePage = lazy(() =>
  import("@/features/team/pages/CreateRolePage")
);
const EditRolePage = lazy(() =>
  import("@/features/team/pages/EditRolePage")
);
const MultiAgentAccessGate = lazy(() =>
  import("@/features/team/components/MultiAgentAccessGate")
);

const Analytics = lazy(() =>
  import("@/features/analytics/pages/Analytics")
);

const AttributesReportsPage = lazy(() =>
  import("@/features/analytics/pages/AttributesReportsPage")
);

const FlowReports = lazy(() =>
  import("@/features/analytics/pages/FlowReports")
);




const ChatbotBuilderLayout = lazy(() =>
  import("@/features/chatbots/components/ChatbotBuilderLayout")
);

const BasicInfo = lazy(() =>
  import("@/features/chatbots/pages/BasicInfo")
);



const DataCollection = lazy(() =>
  import("@/features/chatbots/pages/DataCollection")
);

const SetupIntro = lazy(() =>
  import("@/features/chatbots/pages/SetupIntro")
);


const Deploy = lazy(() =>
  import("@/features/ai-chatbot-v1/pages/Deploy")
);

const AiChatbotAccessGate = lazy(() =>
  import("@/features/chatbots/components/AiChatbotAccessGate")
);

const WhatsAppIntegration = lazy(() =>
  import("@/features/whats-app-crm/pages/WhatsAppIntegration")
);

const WhatsAppTemplates = lazy(() =>
  import("@/features/whats-app-crm/pages/WhatsAppTemplates")
);

const WhatsAppContacts = lazy(() =>
  import("@/features/whats-app-crm/pages/WhatsAppContacts")
);

const WhatsAppAttributes = lazy(() =>
  import("@/features/whats-app-crm/pages/WhatsAppAttributes")
);

const WhatsAppCampaigns = lazy(() =>
  import("@/features/whats-app-crm/pages/WhatsAppCampaigns")
);
const WhatsAppDripCampaigns = lazy(() =>
  import("@/features/whats-app-crm/pages/WhatsAppDripCampaigns")
);

const OptOutSettings = lazy(() =>
  import("@/features/whats-app-crm/pages/OptOutSettings")
);


const ApiKeys = lazy(() =>
  import("@/features/whats-app-crm/pages/ApiKeys")
);
const ApiIntegrationAccessGate = lazy(() =>
  import("@/features/whats-app-crm/components/ApiIntegrationAccessGate")
);

const WorkflowsList = lazy(() =>
  import("@/features/workflow/pages/WorkflowsList")
);

const WorkflowLibrary = lazy(() =>
  import("@/features/workflow/pages/WorkflowLibrary")
);

const Workflow = lazy(() =>
  import("@/features/workflow/pages/Workflow")
);

const ApiManagement = lazy(() =>
  import("@/features/workflow/pages/ApiManagement")
);

const FormBuilder = lazy(() =>
  import("@/features/workflow/pages/FormBuilder")
);

const FlowResponses = lazy(() =>
  import("@/features/workflow/pages/FlowResponses")
);

const WorkflowAccessGate = lazy(() =>
  import("@/features/workflow/components/WorkflowAccessGate")
);


const GlobalLiveChatPage = lazy(() =>
  import("@/features/live-chat/pages/GlobalLiveChatPage").then(module => ({
    default: module.GlobalLiveChatPage,
  }))
);




const CreateCampaignPage = lazy(() =>
  import("@/features/whats-app-crm/pages/CreateCampaignPage")
);

const CampaignDetailsPage = lazy(() =>
  import("@/features/whats-app-crm/pages/CampaignDetailsPage")
);
const CreateDripCampaignPage = lazy(() =>
  import("@/features/whats-app-crm/pages/CreateDripCampaignPage")
);
const DripCampaignDetailsPage = lazy(() =>
  import("@/features/whats-app-crm/pages/DripCampaignDetailsPage")
);

const CreateContactPage = lazy(() =>
  import("@/features/whats-app-crm/pages/CreateContactPage")
);

const PaymentHistory = lazy(() =>
  import("@/features/plan/pages/PaymentHistory")
);

const FreeTools = lazy(() =>
  import("@/features/free-tools/pages/FreeTools")
);

// Real Estate Pages
const PropertyList = lazy(() =>
  import("@/features/real-estate/pages/PropertyList")
);
const PropertyForm = lazy(() =>
  import("@/features/real-estate/pages/PropertyForm")
);
const PropertyLeads = lazy(() =>
  import("@/features/real-estate/pages/PropertyLeads")
);
const RealEstateSetupIntro = lazy(() =>
  import("@/features/real-estate/pages/RealEstateSetupIntro")
);
const RealEstateSetupForm = lazy(() =>
  import("@/features/real-estate/pages/RealEstateSetupForm")
);



export const routes: RouteObject[] = [
  // Auth Routes
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/remote-login",
    element: <RemoteLoginPage />,
  },
  {
    path: "/register",
    element: <RegisterPage />,
  },
  {
    path: "/verify-otp",
    element: <VerifyOtpPage />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/confirm-password/:token",
    element: <ResetPasswordPage />,
  },
  {
    path: "/",
    element: <LoginPage />,
  },

  {
    path: "/analytics",
    element: (
      <ProtectedRoute>
        <Analytics />
      </ProtectedRoute>
    ),
  },

  // Chatbot Builder with Nested Routes (Persistent Sidebar)
  {
    path: "/chatbot-builder/:id",
    element: (
      <ProtectedRoute>
        <ChatbotBuilderLayout />
      </ProtectedRoute>
    ),
    children: [
      // AI Chatbot Steps
      {
        path: "",
        element: <PermissionRoute permission="chatbots.view"><BasicInfo /></PermissionRoute>,
      },
      {
        path: "plans",
        element: <PermissionRoute permission="subscription.view"><Plans /></PermissionRoute>,
      },
      {
        path: "basic-info",
        element: <PermissionRoute permission="chatbots.view"><BasicInfo /></PermissionRoute>,
      },

      {
        path: "data-collection",
        element: <PermissionRoute permission="chatbots.view"><DataCollection /></PermissionRoute>,
      },

      {
        path: "deploy",
        element: <PermissionRoute permission="chatbots.view"><Deploy /></PermissionRoute>,
      },
      {
        path: "intro",
        element: <PermissionRoute permission="chatbots.view"><SetupIntro /></PermissionRoute>,
      },
      {
        path: "onboarding",
        children: [
          {
            path: "basic-info",
            element: <PermissionRoute permission="chatbots.view"><BasicInfo /></PermissionRoute>,
          },
          {
            path: "data-collection",
            element: <PermissionRoute permission="chatbots.view"><DataCollection /></PermissionRoute>,
          },
          {
            path: "deploy",
            element: <PermissionRoute permission="chatbots.view"><Deploy /></PermissionRoute>,
          },
        ]
      },

      // WhatsApp Routes
      {
        path: "whatsapp",
        children: [
          {
            path: "",
            element: <PermissionRoute permission="whatsapp_connection.view"><WhatsAppIntegration /></PermissionRoute>,
          },
          {
            path: "dashboard",
            element: <WhatsAppIntegration />,
          },
          {
            path: "templates",
            children: [
              {
                path: "",
                element: <PermissionRoute permission="whatsapp_templates.view"><WhatsAppTemplates /></PermissionRoute>,
              },
              {
                path: "create",
                element: <PermissionRoute permission={["whatsapp_templates.create", "whatsapp_templates.update"]}><WhatsAppTemplateBuilder /></PermissionRoute>,
              },
              {
                path: "edit/:templateId",
                element: <PermissionRoute permission={["whatsapp_templates.create", "whatsapp_templates.update"]}><WhatsAppTemplateBuilder /></PermissionRoute>,
              },
              {
                path: "view/:templateId",
                element: <PermissionRoute permission={["whatsapp_templates.view"]}><ViewTemplatePage /></PermissionRoute>,
              },
              {
                path: "samples",
                element: <PermissionRoute permission={["whatsapp_templates.view"]}><TemplateLibrary /></PermissionRoute>,
              },
            ],
          },
          {
            path: "contacts",
            children: [
              {
                path: "",
                element: <PermissionRoute permission="contacts.view"><WhatsAppContacts /></PermissionRoute>,
              },
              {
                path: "create",
                element: <PermissionRoute permission="contacts.create"><CreateContactPage /></PermissionRoute>,
              },
              {
                path: "edit",
                element: <PermissionRoute permission="contacts.update"><CreateContactPage /></PermissionRoute>,
              },
            ],
          },
          {
            path: "attributes",
            element: <PermissionRoute permission="attribute.view"><WhatsAppAttributes /></PermissionRoute>,
          },
          {
            path: "opt-inout-settings",
            element: <OwnerRoute><OptOutSettings /></OwnerRoute>,
          },
          {
            path: "api-keys",
            element: <PermissionRoute permission="api_integration_key.view"><ApiKeys /></PermissionRoute>,
          },
          {
            path: "campaigns",
            children: [
              {
                path: "",
                element: <PermissionRoute permission="whatsapp_campaign.view"><WhatsAppCampaigns /></PermissionRoute>,
              },
              {
                path: "create",
                element: <PermissionRoute permission="whatsapp_campaign.create"><CreateCampaignPage /></PermissionRoute>,
              },
              {
                path: "edit/:campaignId",
                element: <PermissionRoute permission="whatsapp_campaign.update"><CreateCampaignPage /></PermissionRoute>,
              },
              {
                path: ":campaignId",
                element: <PermissionRoute permission="whatsapp_campaign.view"><CampaignDetailsPage /></PermissionRoute>,
              },
            ],
          },
          {
            path: "drip-campaigns",
            children: [
              {
                path: "",
                element: <PermissionRoute permission="whatsapp_campaign.view"><WhatsAppDripCampaigns /></PermissionRoute>,
              },
              {
                path: "create",
                element: <PermissionRoute permission="whatsapp_campaign.create"><CreateDripCampaignPage /></PermissionRoute>,
              },
              {
                path: "edit/:dripCampaignId",
                element: <PermissionRoute permission="whatsapp_campaign.update"><CreateDripCampaignPage /></PermissionRoute>,
              },
              {
                path: ":dripCampaignId",
                element: <PermissionRoute permission="whatsapp_campaign.view"><DripCampaignDetailsPage /></PermissionRoute>,
              },
            ],
          },
          {
            path: "workflows",
            element: <WorkflowAccessGate />,
            children: [
              {
                path: "",
                element: <PermissionRoute permission="workflow.view"><WorkflowsList /></PermissionRoute>,
              },
              {
                path: "library",
                element: <PermissionRoute permission="workflow.view"><WorkflowLibrary /></PermissionRoute>,
              },
              {
                path: ":workflowId",
                element: <PermissionRoute permission="workflow.view"><Workflow /></PermissionRoute>,
              },
              {
                path: "api-management",
                element: <PermissionRoute permission="workflow_api_management.view"><ApiManagement /></PermissionRoute>,
              },
              {
                path: "flow-builder",
                element: <PermissionRoute permission={["flow_builder.view", "flow_builder.create", "flow_builder.update"]}><FormBuilder /></PermissionRoute>,
              },
              {
                path: "flow-builder/:flowId/responses",
                element: (
                  <PermissionRoute permission={["flow_builder.view", "flow_builder.create", "flow_builder.update"]}>
                    <FlowResponses />
                  </PermissionRoute>
                ),
              },
            ],
          },
          {
            path: "api-integration",
            element: <PermissionRoute permission="api_integration_key.view"><ApiIntegrationAccessGate /></PermissionRoute>,
          },
        ]
      },

      // Free Tools Route
      {
        path: "free-tools",
        element: <FreeTools />,
      },
      // WhatsApp Link Generator Route
      {
        path: "whatsapp-link-generator",
        element: (
          <Suspense fallback={<div>Loading...</div>}>
            <WhatsAppLinkGenerator />
          </Suspense>
        ),
      },
      // WhatsApp QR Generator Route
      {
        path: "whatsapp-qr-generator",
        element: (
          <Suspense fallback={<div>Loading...</div>}>
            <WhatsAppQRGenerator />
          </Suspense>
        ),
      },
      // WhatsApp Widget Generator Route
      {
        path: "whatsapp-widget-generator",
        element: (
          <Suspense fallback={<div>Loading...</div>}>
            <WhatsAppWidgetGenerator />
          </Suspense>
        ),
      },
      // Live Chat Route
      {
        path: "live-chat",
        element: <PermissionRoute permission="live_chat.manage"><GlobalLiveChatPage /></PermissionRoute>,
      },
      {
        path: "instagram",
        children: [
          {
            path: "connection",
            element: <PermissionRoute permission="chatbots.view"><InstagramConnectionPage /></PermissionRoute>,
          },
          {
            path: "automation",
            element: <PermissionRoute permission="chatbots.view"><InstagramAutomationListPage /></PermissionRoute>,
          },
          {
            path: "automation/create",
            element: <PermissionRoute permission="chatbots.view"><InstagramAutomationBuilderPage /></PermissionRoute>,
          },
          {
            path: "automation/:ruleId/edit",
            element: <PermissionRoute permission="chatbots.view"><InstagramAutomationBuilderPage /></PermissionRoute>,
          },
          {
            path: "automation/:ruleId",
            element: <PermissionRoute permission="chatbots.view"><InstagramAutomationDetailPage /></PermissionRoute>,
          },
        ],
      },
      // AI Chatbot v1 Routes
      {
        path: "ai-chatbot",
        element: <AiChatbotAccessGate />,
        children: [
          {
            path: "knowledge-sources",
            children: [
              {
                path: "",
                element: <PermissionRoute permission="training_content.view"><KnowledgeSources /></PermissionRoute>,
              },
              {
                path: ":sourceId",
                element: <PermissionRoute permission="training_content.view"><KnowledgeSourceDetail /></PermissionRoute>,
              },
            ],
          },
          {
            path: "settings",
            element: <PermissionRoute permission="training_content.view"><AiChatbotV1Settings /></PermissionRoute>,
          },
          {
            path: "leads",
            element: <PermissionRoute permission="training_content.view"><AiChatbotV1Leads /></PermissionRoute>,
          },
          {
            path: "test-chat",
            element: <PermissionRoute permission="training_content.view"><AiChatbotV1TestChat /></PermissionRoute>,
          },
          {
            path: "usage",
            element: <PermissionRoute permission="training_content.view"><AiChatbotV1Usage /></PermissionRoute>,
          },
        ]
      },
      // Dashboard content inside builder
      {
        path: "analytics",
        children: [
          {
            path: "",
            element: <PermissionRoute permission="analytics.view"><Analytics /></PermissionRoute>,
          },
          {
            path: "leads",
            element: <PermissionRoute permission="analytics.view"><AttributesReportsPage /></PermissionRoute>,
          },
          {
            path: "flow-reports",
            element: <PermissionRoute permission="analytics.view"><FlowReports /></PermissionRoute>,
          },
        ],
      },
      {
        path: "plans",
        element: <PermissionRoute permission="subscription.view"><Plans /></PermissionRoute>,
      },
      {
        path: "settings",
        element: <PermissionRoute permission="settings.view"><Setting /></PermissionRoute>,
      },
      {
        path: "admin-settings",
        element: <PermissionRoute permission="settings.view"><AdminSettingsPage /></PermissionRoute>,
      },
      {
        element: <MultiAgentAccessGate />,
        children: [
          {
            path: "team",
            element: <PermissionRoute permission="team_management.view"><TeamManagement /></PermissionRoute>,
          },
          {
            path: "roles",
            element: <PermissionRoute permission="team_management.view"><RolesAndPermissions /></PermissionRoute>,
          },
          {
            path: "roles/create",
            element: <PermissionRoute permission="team_management.edit"><CreateRolePage /></PermissionRoute>,
          },
          {
            path: "roles/edit/:roleId",
            element: <PermissionRoute permission="team_management.edit"><EditRolePage /></PermissionRoute>,
          },
        ],
      },
      {
        path: "payment-history",
        element: <PermissionRoute permission="subscription.view"><PaymentHistory /></PermissionRoute>,
      },
      {
        path: "real-estate",
        children: [
          {
            path: "",
            element: <PropertyList />,
          },
          {
            path: "setup-intro",
            element: <RealEstateSetupIntro />,
          },
          {
            path: "setup",
            element: <RealEstateSetupForm />,
          },
          {
            path: "create",
            element: <PropertyForm />,
          },
          {
            path: "edit/:propertyId",
            element: <PropertyForm />,
          },
          {
            path: "leads",
            element: <PropertyLeads />,
          },
        ],
      },
    ]
  },

  {
    path: "settings",
    element: (
      <ProtectedRoute>
        <Setting />
      </ProtectedRoute>
    ),
  },
  {
    path: "*",
    element: <NotFound />,
  },
];
